import { useCallback, useState } from 'react';
import type { CampaignLaunchAnswers } from 'fym-shared/src/music24/campaignLaunch';
import { useLaunchPromoPostsMutation } from '../../../../../../../redux/api/fymAPI';
import { launchOutcomeOf, pendingLaunchOf, type LaunchOutcome, type PendingLaunch } from './launchModel';

/** `ask`: the dry run and a launch with no question. `answer`: the launch from the open dialog. */
export type LaunchStage = 'ask' | 'answer';

/**
 * Runs one step of a launch for the screen: it shows the busy state and says an error in the way
 * of the screen. It answers true when the step did not throw.
 */
export type LaunchRun = (place: string, stage: LaunchStage, work: () => Promise<void>) => Promise<boolean>;

interface UsePostsLaunchProps {
  accountId: number;
  run: LaunchRun;
}

/**
 * The one flow that schedules many drafts (the plan "Schedule all N posts" and the Calendar
 * "Schedule all drafts of this day"; owner decision 2026-10-11). A dry run says which questions
 * the platforms need. With no question the posts are scheduled at once; else the dialog
 * (`PostsLaunchQuestions`) asks one time and `answerLaunch` schedules them. The server schedules
 * what it can and names the rest: the result is `launchOutcome` (`PostsLaunchSummary`), never one
 * error for all posts.
 */
export function usePostsLaunch({ accountId, run }: UsePostsLaunchProps) {
  const [launchPosts] = useLaunchPromoPostsMutation();
  const [pendingLaunch, setPendingLaunch] = useState<PendingLaunch | null>(null);
  const [launchOutcome, setLaunchOutcome] = useState<LaunchOutcome | null>(null);

  const launch = useCallback(
    (place: string, postUuids: string[]) =>
      run(place, 'ask', async () => {
        setLaunchOutcome(null);
        const dry = await launchPosts({ accountId, postUuids, dryRun: true }).unwrap();
        if (dry.questions.length > 0) {
          setPendingLaunch(pendingLaunchOf(place, postUuids, dry));
          return;
        }
        setLaunchOutcome(launchOutcomeOf(place, await launchPosts({ accountId, postUuids }).unwrap()));
      }),
    [accountId, launchPosts, run]
  );

  /** The answers of the dialog: they go on every draft of their platform, then the launch runs. */
  const answerLaunch = useCallback(
    async (answers: CampaignLaunchAnswers): Promise<boolean> => {
      if (pendingLaunch === null) {
        return false;
      }
      const { place, postUuids } = pendingLaunch;
      const ok = await run(place, 'answer', async () => {
        setLaunchOutcome(launchOutcomeOf(place, await launchPosts({ accountId, postUuids, answers }).unwrap()));
      });
      if (ok) {
        setPendingLaunch(null);
      }
      return ok;
    },
    [accountId, launchPosts, pendingLaunch, run]
  );

  const cancelLaunch = useCallback(() => setPendingLaunch(null), []);
  const dismissLaunchOutcome = useCallback(() => setLaunchOutcome(null), []);

  return { launch, answerLaunch, cancelLaunch, dismissLaunchOutcome, pendingLaunch, launchOutcome };
}

export type PostsLaunch = ReturnType<typeof usePostsLaunch>;
